/**
 * Delivery-related UX texts.
 * Explicit, unambiguous, backend-driven. All times are Moscow (МСК).
 * Mapping: backend state → UI text.
 */

const TZ_SUFFIX = ' (МСК)';

// ——— Section titles ———
export const deliveryCopy = {
  sectionDeliveryConditions: 'Условия доставки',
  sectionDateAndTime: 'Дата и время доставки',
  sectionDateAndTimeHint: 'Дата и время указаны по московскому времени (МСК).',
  sectionAddress: 'Адрес доставки',
  labelDeliveryTypeDelivery: 'Доставка',
  labelDeliveryTypePickup: 'Самовывоз',
  labelDateRequired: 'Дата',
  labelTimeRequired: 'Время',
  labelDeliveryCost: 'Доставка',
} as const;

// ——— Delivery by readiness ———
export const deliveryByReadiness = {
  title: 'Доставка по готовности букета',
  description:
    'Мы доставим букет в течение 1–2 часов после сборки. Точное время доставки нельзя выбрать — оно зависит от готовности заказа. Это не мгновенная доставка: сначала собираем букет, затем везём по адресу.',
  hint: 'Подходит, если важна скорость, а не конкретный час.',
} as const;

// ——— Conditions block (backend display values interpolated) ———
export function deliveryConditionsWorkingHours(workingHours: string): string {
  return `Рабочие часы доставки по московскому времени: ${workingHours}.`;
}

export function deliveryConditionsAfterHours(
  afterHoursStart: string,
  afterHoursFee: string
): string {
  return `Доставка вне рабочих часов (после ${afterHoursStart} ${TZ_SUFFIX}): доплата ${afterHoursFee}, обязательное согласование с менеджером. Заказ не отклоняется — менеджер подтвердит возможность и время.`;
}

export function deliveryConditionsAvgTime(_avgTime: string): string {
  return 'Обычно доставляем в течении двух часов после готовности заказа.';
}

export const deliveryConditionsZonesFallback = 'Тарифы по зонам загружаются.';

// ——— Banner / explanation (backend sends full explanationText; these are fallbacks by state) ———
export function bannerEarliestDelivery(earliestDate: string, earliestTime: string): string {
  return `По московскому времени сейчас доступна доставка не раньше ${formatDateForDisplay(earliestDate)} в ${earliestTime}${TZ_SUFFIX}. Выберите эту дату и время или позже.`;
}

export function bannerAfterHoursSelected(afterHoursStart: string, afterHoursFee: string): string {
  return `Выбрана доставка вне рабочих часов (после ${afterHoursStart} МСК). Доплата ${afterHoursFee}. Менеджер свяжется с вами и подтвердит возможность доставки — заказ не отменяется.`;
}

export function bannerZoneRequiresApproval(zoneName: string): string {
  return `Доставка в зону «${zoneName}» возможна по договорённости с менеджером. Стоимость и время уточнят после оформления заказа.`;
}

// ——— Validation errors ———
export const validationDateRequired = 'Укажите дату доставки.';

export const validationTimeRequired = 'Укажите время доставки.';

export function validationDateTooEarly(earliestDate: string, earliestTime: string): string {
  return `Выбранная дата раньше доступной. Первая доступная доставка по московскому времени: ${formatDateForDisplay(earliestDate)} в ${earliestTime}. Выберите эту дату или позже.`;
}

export function validationTimeTooEarly(minTime: string): string {
  return `Выбранное время раньше доступного для этой даты. Минимальное время по московскому времени: ${minTime}${TZ_SUFFIX}.`;
}

// ——— Loading / empty ———
export const loadingSlots = 'Загрузка вариантов даты и времени доставки…';
export const selectDateFirst = 'Сначала выберите дату';
export const noSlotsPlaceholder = 'Загрузка…';

// ——— Order summary (Profile, etc.) ———
export const orderDeliveryTypeDelivery = 'Доставка';
export const orderDeliveryTypePickup = 'Самовывоз';
export function orderDeliveryDateTimeLabel(): string {
  return `Дата и время (МСК)`;
}

// ——— About page ———
export function aboutDeliveryIntro(
  city: string,
  workingHours: string,
  avgTime: string
): string {
  return `Доставка по ${city}. Работаем по московскому времени ${workingHours}. Среднее время доставки после готовности заказа: ${avgTime}.`;
}

export function aboutDeliveryAfterHours(
  workingHours: string,
  afterHoursStart: string,
  afterHoursFee: string
): string {
  return `Доставка в рабочие часы (МСК): ${workingHours}. Доставка вне этих часов (после ${afterHoursStart} МСК): доплата ${afterHoursFee}, возможность и точное время согласовываются с менеджером. Заказ не отклоняется.`;
}

export const aboutDeliveryNotesDefault = [
  'Заказы с доставкой в ночные часы оформляйте до 21:00 по московскому времени.',
  'Минимальной суммы заказа нет.',
] as const;

export const aboutZoneOtherPrice = 'по договорённости с менеджером';

/** Format YYYY-MM-DD as "DD.MM.YYYY" for display (no "today/tomorrow" — explicit date). */
export function formatDateForDisplay(ymd: string): string {
  if (!ymd || ymd.length < 10) return ymd;
  const [y, m, d] = [ymd.slice(0, 4), ymd.slice(5, 7), ymd.slice(8, 10)];
  return `${d}.${m}.${y}`;
}
